import React from 'react';
import { MapPin, Phone, Clock, Facebook, Instagram, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_voiture-lamarsa/artifacts/krmorh2a_image.png';

const Footer = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { href: '#home', label: t('nav.home') },
    { href: '#about', label: t('nav.about') },
    { href: '#fleet', label: t('nav.fleet') },
    { href: '#reviews', label: t('nav.reviews') },
    { href: '#contact', label: t('nav.contact') },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer
      data-testid="footer"
      className="bg-[#606060] text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="mb-4">
              <img 
                src={LOGO_URL} 
                alt="UNI CAR - rent a car" 
                className="h-12 sm:h-16 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-white/70 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/UNIVAERSELRENTCAR/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="p-2 sm:p-2.5 rounded-full bg-white/10 hover:bg-[#00bfa5] transition-colors"
              >
                <Facebook className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
              <a
                href="#"
                data-testid="footer-instagram"
                className="p-2 sm:p-2.5 rounded-full bg-white/10 hover:bg-[#00bfa5] transition-colors"
              >
                <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="hidden sm:block">
            <h3 className="text-base sm:text-lg font-semibold mb-4 sm:mb-6 font-['Outfit']">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-2 sm:space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-white/70 hover:text-[#00bfa5] transition-colors flex items-center gap-2 text-sm sm:text-base"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00bfa5]" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold mb-4 sm:mb-6 font-['Outfit']">
              {t('footer.contact')}
            </h3>
            <ul className="space-y-3 sm:space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#00bfa5] mt-0.5 flex-shrink-0" />
                <span className="text-white/70 text-sm sm:text-base">
                  12 rue de l'indépendance,<br />
                  2070 La Marsa, Tunisie
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-[#00bfa5] flex-shrink-0" />
                <a
                  href="tel:+21620505585"
                  className="text-white/70 hover:text-[#00bfa5] transition-colors text-sm sm:text-base"
                >
                  +216 20 505 585
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#00bfa5] mt-0.5 flex-shrink-0" />
                <span className="text-white/70 text-sm sm:text-base">
                  {t('contact.hoursValue')}
                </span>
              </li>
            </ul>
          </div>

          {/* Facebook Embed */}
          <div className="sm:col-span-2 lg:col-span-1">
            <h3 className="text-base sm:text-lg font-semibold mb-4 sm:mb-6 font-['Outfit']">
              {t('footer.followUs')}
            </h3>
            <div
              data-testid="facebook-embed"
              className="bg-white/10 rounded-xl p-3 sm:p-4 backdrop-blur-sm"
            >
              <a
                href="https://www.facebook.com/UNIVAERSELRENTCAR/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white hover:text-[#00bfa5] transition-colors"
              >
                <Facebook className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-sm sm:text-base">UNI CAR</p>
                  <p className="text-xs sm:text-sm text-white/60 truncate">@UNIVAERSELRENTCAR</p>
                </div>
                <ExternalLink className="w-4 h-4 ml-auto flex-shrink-0" />
              </a>
              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/10">
                <p className="text-xs sm:text-sm text-white/60 mb-2">
                  Suivez-nous sur Facebook pour les dernières offres!
                </p>
                <a
                  href="https://www.facebook.com/UNIVAERSELRENTCAR/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs sm:text-sm text-[#00bfa5] hover:underline"
                >
                  Voir notre page <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/10 text-center">
          <p className="text-white/50 text-xs sm:text-sm">
            &copy; {new Date().getFullYear()} UNI CAR - Location de Voitures à La Marsa. {t('footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
